'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Shield, Cookie, X, ChevronDown, ChevronUp } from 'lucide-react'
import Link from 'next/link'

const CONSENT_COOKIE = 'lk_cookie_consent'
const CONSENT_VERSION = '1' // Increment when policy changes to re-prompt

export type ConsentPreferences = {
  essential: true // Always true
  analytics: boolean
  marketing: boolean
}

function getConsent(): ConsentPreferences | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = document.cookie
      .split('; ')
      .find(c => c.startsWith(`${CONSENT_COOKIE}=`))
    if (!raw) return null
    const parsed = JSON.parse(decodeURIComponent(raw.split('=')[1]))
    if (parsed.version !== CONSENT_VERSION) return null
    return parsed.preferences
  } catch {
    return null
  }
}

function setConsent(preferences: ConsentPreferences) {
  const value = JSON.stringify({ version: CONSENT_VERSION, preferences, timestamp: new Date().toISOString() })
  // Set consent cookie for 365 days — this is an essential cookie so no consent needed for it
  document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(value)}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax; Secure`
  // Dispatch event so layout can react
  window.dispatchEvent(new CustomEvent('cookie-consent-update', { detail: preferences }))
}

export function hasAnalyticsConsent(): boolean {
  const consent = getConsent()
  return consent?.analytics ?? false
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  })

  useEffect(() => {
    const existing = getConsent()
    if (!existing) {
      // Small delay so the page renders first
      const timer = setTimeout(() => setVisible(true), 1500)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleAcceptAll = () => {
    const prefs: ConsentPreferences = { essential: true, analytics: true, marketing: true }
    setConsent(prefs)
    setVisible(false)
  }

  const handleRejectNonEssential = () => {
    const prefs: ConsentPreferences = { essential: true, analytics: false, marketing: false }
    setConsent(prefs)
    setVisible(false)
  }

  const handleSavePreferences = () => {
    setConsent(preferences)
    setVisible(false)
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
        >
          <div className="max-w-2xl mx-auto rounded-2xl border border-white/[0.08] bg-black/90 backdrop-blur-2xl shadow-[0_-8px_40px_rgba(0,0,0,0.5)] overflow-hidden">
            {/* Header */}
            <div className="p-5 pb-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                    <Cookie className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">We value your privacy</h3>
                    <p className="text-white/40 text-xs mt-0.5">
                      We use cookies to improve your experience. You can customize your preferences below.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleRejectNonEssential}
                  className="text-white/30 hover:text-white/60 transition-colors shrink-0 mt-1"
                  aria-label="Reject non-essential cookies"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Expandable Details */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-4 space-y-3">
                    {/* Essential — always on */}
                    <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                      <div>
                        <p className="text-white text-xs font-medium flex items-center gap-1.5">
                          <Shield className="w-3.5 h-3.5 text-emerald-400" /> Essential
                        </p>
                        <p className="text-white/30 text-[11px] mt-0.5">Authentication, security, core functionality</p>
                      </div>
                      <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider">Always On</span>
                    </div>

                    {/* Analytics */}
                    <label className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] cursor-pointer group">
                      <div>
                        <p className="text-white text-xs font-medium">Analytics</p>
                        <p className="text-white/30 text-[11px] mt-0.5">Anonymous usage data via Google Analytics &amp; Vercel</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={e => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                        className="w-4 h-4 rounded accent-primary cursor-pointer"
                      />
                    </label>

                    {/* Marketing */}
                    <label className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] cursor-pointer group">
                      <div>
                        <p className="text-white text-xs font-medium">Marketing &amp; Retargeting</p>
                        <p className="text-white/30 text-[11px] mt-0.5">Facebook Pixel, TikTok Pixel, Google Ads on redirect pages</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={e => setPreferences(p => ({ ...p, marketing: e.target.checked }))}
                        className="w-4 h-4 rounded accent-primary cursor-pointer"
                      />
                    </label>

                    <p className="text-white/20 text-[10px] text-center pt-1">
                      Read our full{' '}
                      <Link href="/legal/cookies" className="text-primary/60 hover:text-primary underline">Cookie Policy</Link>
                      {' '}and{' '}
                      <Link href="/legal/privacy" className="text-primary/60 hover:text-primary underline">Privacy Policy</Link>
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <div className="p-4 pt-2 flex items-center gap-2">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1 px-3 py-2 text-[11px] text-white/40 hover:text-white/70 transition-colors font-medium"
              >
                {showDetails ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                {showDetails ? 'Less' : 'Customize'}
              </button>
              <div className="flex-1" />
              <button
                onClick={handleRejectNonEssential}
                className="px-4 py-2 text-xs text-white/50 hover:text-white border border-white/[0.08] hover:border-white/[0.15] rounded-lg transition-all font-medium"
              >
                Reject All
              </button>
              {showDetails ? (
                <button
                  onClick={handleSavePreferences}
                  className="px-4 py-2 text-xs text-white bg-primary hover:bg-primary/90 rounded-lg transition-all font-semibold shadow-lg shadow-primary/20"
                >
                  Save Preferences
                </button>
              ) : (
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-xs text-white bg-primary hover:bg-primary/90 rounded-lg transition-all font-semibold shadow-lg shadow-primary/20"
                >
                  Accept All
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
